import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getActivityZones } from '../services';
import { ActivityZone } from '../types';
import {
  getActivityIdsPerWeek,
  getRunningActivities,
  parseAllActivities,
} from '../utils/activities';
import { WEEKNUMBER } from '../utils/date';
import { loadState, saveState } from '../utils/localStorage';
import { useActivities } from './useActivities';
import { useGetAccessToken } from './useGetAccessToken';

const staleTime = 20 * 60 * 1000; // 20 minutes
const KEY = 'zones-activity';

let initialValue: { [key: string]: number[] } = {};
[...Array(WEEKNUMBER).keys()].forEach((week) => {
  initialValue[week] = [];
});

export const useActivityZones = () => {
  const { activities } = useActivities();

  const allActivities = activities.map(parseAllActivities);

  const runningActivities = getRunningActivities(allActivities);

  const runningActivitiesIdByWeek = runningActivities.reduce(
    getActivityIdsPerWeek,
    { ...initialValue }
  );

  const runningActivitiesIds = Object.values(runningActivitiesIdByWeek);

  const getAllZones = (access_token: string) => {
    return Promise.all(
      runningActivitiesIds.map((ids) =>
        Promise.all(ids.map((id) => getActivityZones(id, access_token)))
      )
    );
  };

  const { access_token } = useGetAccessToken();

  const initialData: ActivityZone[][][] = loadState(KEY);

  const {
    data: zones,
    isLoading,
    error,
  } = useQuery({
    queryKey: [`zones - activity`],
    queryFn: () => getAllZones(access_token || ''),
    staleTime,
    enabled: access_token !== undefined,
    initialData,
  });

  useEffect(() => {
    if (!error && !isLoading) {
      saveState(KEY, zones);
    }
  }, []);

  return { zones, isLoading, error };
};
